<script module lang="ts">
  import type { Snippet } from 'svelte';

  type Align = 'left' | 'right' | 'center';
  // Not parametrized by row type on purpose: a Svelte 5 generic component's
  // T can only be inferred from a covariant prop (rows), and a cell
  // snippet's row parameter is contravariant — trying to also infer T from
  // `columns[].cell` fights that and T collapses to the constraint default.
  // Each page still gets full type-checking on its own `{#snippet cell(row:
  // WorkLog)}` bodies; only the "this column belongs to this row type" link
  // is unchecked, which is fine for a handful of same-file call sites.
  export type Column = {
    key: string;
    label: string;
    align?: Align;
    width?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell?: Snippet<[any]>;
  };
</script>

<script lang="ts" generics="T extends object">
  // Generic .tym-table wrapper — one table markup shared by every list page.
  // Columns carry their own cell snippet (rendered per row); a column with
  // no snippet just prints the raw field. Sticky header, optional row click,
  // empty-state and footer slots. No data fetching/filtering here — pages
  // own their queries and pass the resulting rows in.
  import EmptyState from './EmptyState.svelte';

  const {
    columns,
    rows,
    rowKey,
    onRowClick,
    emptyTitle = 'Nothing here',
    emptyBody,
    footer,
    dense = false,
    loading = false,
    skeletonRows = 6,
    mobileCards = false,
    mobileCard,
    mobileColumns
  }: {
    columns: Column[];
    rows: T[];
    rowKey?: (row: T) => string | number;
    onRowClick?: (row: T) => void;
    emptyTitle?: string;
    emptyBody?: string;
    footer?: Snippet;
    /** Tighter row rhythm — for record tables you scan rather than read. */
    dense?: boolean;
    /** Renders shimmer rows instead of `rows`, so the page keeps its shape
        while the query is in flight instead of collapsing to one line. */
    loading?: boolean;
    skeletonRows?: number;
    /** Below `sm`, swap the table for a stacked card list built from the
        columns (see cardTitleCol/cardMetaCols). A 10-column record table on a
        390px phone is a horizontal-scroll puzzle; the same three fields
        stacked is the same information you can actually read. */
    mobileCards?: boolean;
    /** Full control over the card body, when the derived one won't do.
        Implies `mobileCards`. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mobileCard?: Snippet<[any]>;
    /** Column keys to build the card from (first = title, rest = meta),
        overriding the "every labelled column, in order" default. */
    mobileColumns?: string[];
  } = $props();

  const alignClass: Record<Align, string> = { left: 'text-left', right: 'text-right', center: 'text-center' };

  // Card mode derives its layout from the columns rather than asking every
  // page to restate them: an unlabelled left column (status lamp, row
  // checkbox) leads, the labelled columns supply the title and up to three
  // meta pairs, and an unlabelled right-aligned column (the row menu) trails.
  const cardsOn = $derived(mobileCards || !!mobileCard);
  const labelled = $derived(
    mobileColumns
      ? (mobileColumns.map((k) => columns.find((c) => c.key === k)).filter(Boolean) as Column[])
      : columns.filter((c) => c.label.trim() !== '')
  );
  const cardLeadCol = $derived(columns.find((c) => c.label.trim() === '' && c.align !== 'right' && c.cell));
  const cardTrailCol = $derived(columns.find((c) => c.label.trim() === '' && c.align === 'right' && c.cell));
  const cardTitleCol = $derived(labelled[0]);
  // An explicit `mobileColumns` is taken at its word; the derived list is
  // capped, because a ten-column record table stacked whole is just the
  // scroll problem turned vertical.
  const cardMetaCols = $derived(mobileColumns ? labelled.slice(1) : labelled.slice(1, 4));

  function cardText(col: Column, row: T): string {
    return String((row as Record<string, unknown>)[col.key] ?? '—');
  }

  const CARD_CLASS = 'flex items-start gap-2.5 border-b border-toyama-cloudy-gray/60 px-3.5 py-3 last:border-b-0';

  // Ragged but deterministic widths — uniform bars read as a rendering
  // artefact, varied ones read as text that hasn't arrived yet.
  const SKELETON_WIDTHS = ['72%', '54%', '86%', '46%', '64%', '78%'];
  function skeletonWidth(row: number, col: number): string {
    return SKELETON_WIDTHS[(row * 3 + col * 2) % SKELETON_WIDTHS.length];
  }
</script>

<div class="tym-table-wrap">
  {#if cardsOn}
    <div class="sm:hidden" aria-busy={loading}>
      {#if loading}
        {#each { length: skeletonRows } as _, r (r)}
          <div class="border-b border-toyama-cloudy-gray/60 px-3.5 py-3 last:border-b-0">
            <span class="tym-skeleton" style="width:{skeletonWidth(r, 0)}"></span>
            <span class="tym-skeleton mt-2 block" style="width:{skeletonWidth(r, 1)}"></span>
          </div>
        {/each}
      {:else}
        {#each rows as row, i (rowKey ? rowKey(row) : i)}
          <!-- Two branches rather than a dynamic role/tabindex: a card can
               hold a checkbox or a link, so it can't be a <button>, and a
               non-clickable list shouldn't announce its rows as one. -->
          {#if onRowClick}
            <div
              class="{CARD_CLASS} cursor-pointer active:bg-surface-2"
              role="button"
              tabindex="0"
              onclick={() => onRowClick(row)}
              onkeydown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRowClick(row);
                }
              }}
            >
              {@render cardBody(row)}
            </div>
          {:else}
            <div class={CARD_CLASS}>{@render cardBody(row)}</div>
          {/if}
        {/each}
      {/if}
    </div>
  {/if}
  <div class="tym-table-scroll {cardsOn ? 'hidden sm:block' : ''}">
    <table class="tym-table {dense ? 'tym-table--dense' : ''}" aria-busy={loading}>
      <thead>
        <tr>
          {#each columns as col (col.key)}
            <th class="sticky top-0 {alignClass[col.align ?? 'left']}" style={col.width ? `width:${col.width}` : ''}>
              {col.label}
            </th>
          {/each}
        </tr>
      </thead>
      <tbody>
        {#if loading}
          {#each { length: skeletonRows } as _, r (r)}
            <tr>
              {#each columns as col, c (col.key)}
                <td class={alignClass[col.align ?? 'left']}>
                  <span class="tym-skeleton" style="width:{skeletonWidth(r, c)}"></span>
                </td>
              {/each}
            </tr>
          {/each}
        {:else}
          {#each rows as row, i (rowKey ? rowKey(row) : i)}
            <tr
              class={onRowClick ? 'is-hoverable' : ''}
              style={onRowClick ? 'cursor:pointer' : ''}
              onclick={() => onRowClick?.(row)}
            >
              {#each columns as col (col.key)}
                <td class={alignClass[col.align ?? 'left']}>
                  {#if col.cell}
                    {@render col.cell(row)}
                  {:else}
                    {String((row as Record<string, unknown>)[col.key] ?? '—')}
                  {/if}
                </td>
              {/each}
            </tr>
          {/each}
        {/if}
      </tbody>
    </table>
  </div>
  {#if !loading && rows.length === 0}
    <EmptyState title={emptyTitle} body={emptyBody} />
  {/if}
  {#if footer}
    <div class="flex items-center justify-between gap-2.5 border-t border-toyama-cloudy-gray bg-surface-2 px-3.5 py-2.5">
      {@render footer()}
    </div>
  {/if}
</div>

{#snippet cardBody(row: T)}
  {#if mobileCard}
    {@render mobileCard(row)}
  {:else}
    {#if cardLeadCol?.cell}
      <div class="shrink-0 pt-0.5">{@render cardLeadCol.cell(row)}</div>
    {/if}
    <div class="min-w-0 flex-1">
      {#if cardTitleCol}
        <div class="truncate text-[13.5px] font-medium text-navy">
          {#if cardTitleCol.cell}{@render cardTitleCol.cell(row)}{:else}{cardText(cardTitleCol, row)}{/if}
        </div>
      {/if}
      {#if cardMetaCols.length}
        <div class="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px] text-dark-gray">
          {#each cardMetaCols as col (col.key)}
            <span class="inline-flex min-w-0 max-w-full items-center gap-1 truncate">
              <span class="text-[10.5px] uppercase tracking-[.06em] opacity-70">{col.label}</span>
              {#if col.cell}{@render col.cell(row)}{:else}{cardText(col, row)}{/if}
            </span>
          {/each}
        </div>
      {/if}
    </div>
    {#if cardTrailCol?.cell}
      <div class="shrink-0">{@render cardTrailCol.cell(row)}</div>
    {/if}
  {/if}
{/snippet}
